import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { PawPrint } from "lucide-react";

import type { Context } from "./hulp";
import { Rondje } from "./Tegels";
import type { Tint } from "./tegelDefinities";

/**
 * Een blok op de tabbladen van de klant, in dezelfde stijl als de tegels bij een dier: gekleurd rondje,
 * titel, eventueel een aantal en rechts een actie.
 */
export function Sectie({
  titel,
  icoon,
  tint,
  aantal,
  actie,
  children,
}: {
  titel: string;
  icoon: LucideIcon;
  tint: Tint;
  aantal?: number;
  actie?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="pk-tegel pk-sectie" aria-label={titel}>
      <header className="pk-tegel__kop">
        <Rondje icoon={icoon} tint={tint} grootte="kop" />
        <h3>
          {titel}
          {aantal != null && <span className="pk-sectie__aantal">{aantal}</span>}
        </h3>
        {actie}
      </header>
      <div className="pk-tegel__inhoud">{children}</div>
    </section>
  );
}

/** Een knopje onderaan een blok dat naar meer leidt. */
export function Verder({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="pk-tegel__verder" onClick={onClick}>
      {children}
    </button>
  );
}

/** Een diernaam die naar het tabblad Dieren springt, met dat dier gekozen. */
export function DierLink({
  id,
  naam,
  foto,
  onKiesDier,
}: {
  id: number | null;
  naam: string | null;
  /** De foto van het dier; zonder foto staat er een pootje. */
  foto?: string;
  onKiesDier: (id: number) => void;
}) {
  const merk = foto ? (
    <img className="pk-dierlink__foto" src={foto} alt="" />
  ) : (
    <PawPrint size={12} aria-hidden="true" />
  );
  if (id == null) {
    return (
      <span className="pk-dierlink">
        {merk}
        {naam ?? "—"}
      </span>
    );
  }
  return (
    <button type="button" className="pk-dierlink" onClick={() => onKiesDier(id)}>
      {merk}
      {naam ?? "—"}
    </button>
  );
}

export type PilToon = "groen" | "accent" | "amber" | "rood" | "grijs";

export function Pil({ toon, children }: { toon: PilToon; children: ReactNode }) {
  return (
    <span className="pk-pil" data-toon={toon}>
      {children}
    </span>
  );
}

export function Leeg({ children }: { children: ReactNode }) {
  return <p className="pk-tegel__leeg">{children}</p>;
}

/** Laden of een fout, voor een blok dat zijn eigen gegevens ophaalt. */
export function Laadstaat({
  laden,
  fout,
  onOpnieuw,
  ctx,
}: {
  laden: boolean;
  fout: boolean;
  onOpnieuw?: () => void;
  ctx: Context;
}) {
  if (laden) return <Leeg>{ctx.t("common.loading")}</Leeg>;
  if (!fout) return null;
  return (
    <div className="pk-fout" role="alert">
      <span>{ctx.t("pensionKlant.blad.laadFout")}</span>
      {onOpnieuw && (
        <button type="button" className="pk-knop" onClick={onOpnieuw}>
          {ctx.t("pensionKlant.blad.opnieuw")}
        </button>
      )}
    </div>
  );
}
